import { useState, useEffect } from 'react';

/**
 * Smart FAQ Support Widget - Enhanced with Conversation Memory
 * Floating chat bubble that answers common questions with context awareness
 * - FAQ matching (no cost)
 * - AI fallback for complex questions (low cost)
 * - Personalized responses for logged-in users
 * - Conversation memory (localStorage for anonymous, database for logged-in)
 */

// Production API URL - hardcoded fallback for reliability
const API_URL = import.meta.env.VITE_API_URL || 'https://flood-and-fire-pros.direct/api';

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = localStorage.getItem('ffp_support_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('ffp_support_session_id', sessionId);
  }
  return sessionId;
};

// Load conversation history from localStorage
const loadLocalHistory = (sessionId) => {
  try {
    const key = `ffp_conversation_${sessionId}`;
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading conversation history:', error);
    return [];
  }
};

// Save conversation to localStorage (anonymous users)
const saveToLocalHistory = (sessionId, question, answer) => {
  try {
    const key = `ffp_conversation_${sessionId}`;
    const history = loadLocalHistory(sessionId);
    history.push({
      question,
      answer: answer.answer,
      source: answer.source,
      createdAt: new Date().toISOString()
    });
    // Keep only last 20 conversations to avoid localStorage bloat
    const trimmed = history.slice(-20);
    localStorage.setItem(key, JSON.stringify(trimmed));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

export default function SupportWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionId] = useState(getSessionId());
  const [conversationHistory, setConversationHistory] = useState([]);

  // Load conversation history on mount
  useEffect(() => {
    const loadHistory = async () => {
      const token = localStorage.getItem('token');

      if (token) {
        // Logged-in user: Try to load from database
        try {
          const res = await fetch(`${API_URL}/support/history/${sessionId}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            },
            credentials: 'include'
          });

          if (res.ok) {
            const data = await res.json();
            setConversationHistory(data.history || []);
            return;
          }
        } catch (error) {
          console.error('Error loading database history:', error);
        }
      }

      // Anonymous user or database load failed: Load from localStorage
      const localHistory = loadLocalHistory(sessionId);
      setConversationHistory(localHistory);
    };

    loadHistory();
  }, [sessionId]);

  const handleAsk = async () => {
    if (!question.trim()) {
      setError('Please type a question');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch(`${API_URL}/support/ask`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          question: question.trim(),
          sessionId,
          metadata: {} // Can add ZIP, service type later
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to get answer');
      }

      setAnswer(data);

      // Save to localStorage for anonymous users (database save happens on backend for logged-in)
      if (!token) {
        saveToLocalHistory(sessionId, question.trim(), data);
        // Update conversation history state
        setConversationHistory(prev => [...prev, {
          question: question.trim(),
          answer: data.answer,
          source: data.source,
          createdAt: new Date().toISOString()
        }]);
      }
    } catch (err) {
      console.error('Support widget error:', err);
      setError(err.message || 'Unable to connect. Please try again.');
      setAnswer(null);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickQuestion = async (quickQuestion) => {
    // Set the question text (so user can see what was asked)
    setQuestion(quickQuestion);
    setAnswer(null);
    setError(null);
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const res = await fetch(`${API_URL}/support/ask`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          question: quickQuestion,
          sessionId,
          metadata: {}
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to get answer');
      }

      setAnswer(data);

      // Save to localStorage for anonymous users
      if (!token) {
        saveToLocalHistory(sessionId, quickQuestion, data);
        setConversationHistory(prev => [...prev, {
          question: quickQuestion,
          answer: data.answer,
          source: data.source,
          createdAt: new Date().toISOString()
        }]);
      }
    } catch (err) {
      console.error('Quick question error:', err);
      setError(err.message || 'Unable to connect. Please try again.');
      setAnswer(null);
    } finally {
      setLoading(false);
    }
  };

  const handleActionClick = (link) => {
    setIsOpen(false);
    window.location.href = link;
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAsk();
    }
  };

  const quickQuestions = [
    { text: 'How fast can someone arrive?', icon: '⚡' },
    { text: 'How much does it cost?', icon: '💰' },
    { text: 'Do you work with insurance?', icon: '🏢' },
    { text: 'What areas do you serve?', icon: '📍' }
  ];

  return (
    <>
      {/* Enhanced Floating Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-full shadow-2xl hover:shadow-orange-500/50 hover:scale-110 transition-all duration-300 z-50 flex items-center justify-center text-2xl group"
        aria-label="Support chat"
      >
        {isOpen ? (
          <span className="transform rotate-90 transition-transform duration-300">✕</span>
        ) : (
          <span className="group-hover:scale-125 transition-transform duration-300">💬</span>
        )}
        {/* Pulse ring effect */}
        {!isOpen && (
          <span className="absolute inset-0 rounded-full bg-orange-500 animate-ping opacity-20"></span>
        )}
      </button>

      {/* Enhanced Chat Panel with slide-in animation */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-xl shadow-2xl border border-gray-200 z-50 animate-slideUp">
          {/* Enhanced Header with icon */}
          <div className="bg-gradient-to-br from-brand-navy via-brand-navy-dark to-brand-navy text-white p-5 rounded-t-xl relative overflow-hidden">
            {/* Decorative background pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
            </div>

            <div className="relative flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <span className="text-2xl">🆘</span>
              </div>
              <div>
                <h3 className="font-bold text-xl">Need Help?</h3>
                <p className="text-sm opacity-90">Get instant answers to your questions</p>
              </div>
            </div>
          </div>

          <div className="p-5 max-h-[500px] overflow-y-auto">
            {/* Conversation History - Shows context from previous questions */}
            {conversationHistory.length > 0 && !answer && (
              <div className="mb-5">
                <p className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wide flex items-center gap-2">
                  <span className="text-brand-navy">💬</span>
                  Recent Conversations
                </p>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {conversationHistory.slice(-3).map((item, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs font-semibold text-gray-700 mb-1">Q: {item.question}</p>
                      <p className="text-xs text-gray-600 line-clamp-2">{item.answer}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 my-4">
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                  <span className="text-xs text-gray-400 font-semibold">ASK NEW</span>
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                </div>
              </div>
            )}

            {/* Enhanced Answer Display - NOW AT TOP */}
            {answer && (
              <div className={`p-5 rounded-xl animate-fadeIn mb-5 ${
                answer.priority === 'high'
                  ? 'bg-gradient-to-br from-orange-50 to-orange-100 border-2 border-orange-300'
                  : 'bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200'
              }`}>
                {/* Answer icon based on priority */}
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    answer.priority === 'high' ? 'bg-orange-500' : 'bg-blue-500'
                  }`}>
                    <span className="text-white text-lg">
                      {answer.priority === 'high' ? '⚡' : '💡'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line flex-1 pt-1">
                    {answer.answer}
                  </p>
                </div>

                {/* Enhanced Suggested Actions */}
                {answer.suggestedActions && answer.suggestedActions.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {answer.suggestedActions.map((action, index) => (
                      <button
                        key={index}
                        onClick={() => handleActionClick(action.link)}
                        className="w-full p-3 bg-white border-2 border-orange-400 text-orange-600 rounded-lg font-semibold hover:bg-orange-500 hover:text-white hover:border-orange-600 transition-all duration-200 text-sm shadow-sm hover:shadow-md flex items-center justify-center gap-2"
                      >
                        <span>→</span>
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}

                {/* Enhanced Source Badge */}
                {answer.source && (
                  <div className="mt-4 pt-4 border-t-2 border-white/50">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-600 flex items-center gap-2">
                        <span className="font-semibold">
                          {answer.source === 'faq' ? '📚 FAQ' :
                           answer.source === 'database' ? '👤 Your Account' :
                           answer.source === 'ai' ? '🤖 AI Assistant' :
                           '📖 Help Center'}
                        </span>
                      </p>
                      {answer.confidence && (
                        <div className="flex items-center gap-1">
                          <div className="w-16 h-2 bg-white/50 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-orange-500 rounded-full transition-all duration-500"
                              style={{ width: `${answer.confidence * 100}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-600 font-semibold">
                            {Math.round(answer.confidence * 100)}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Ask Another Question - Clears textarea */}
                <button
                  onClick={() => {
                    setAnswer(null);
                    setQuestion(''); // Clear textarea
                    setError(null);
                  }}
                  className="w-full mt-4 p-2 text-sm text-gray-600 hover:text-orange-600 font-medium transition-colors rounded-lg hover:bg-white/50"
                >
                  ← Ask another question
                </button>
              </div>
            )}

            {/* Enhanced Quick Questions */}
            {!answer && !error && (
              <div className="mb-5">
                <p className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wide flex items-center gap-2">
                  <span className="text-orange-500">⚡</span>
                  Popular Questions
                </p>
                <div className="space-y-2">
                  {quickQuestions.map((q, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuickQuestion(q.text)}
                      className="w-full text-left p-3 bg-gradient-to-r from-gray-50 to-white hover:from-orange-50 hover:to-orange-50 border border-gray-200 hover:border-orange-300 rounded-lg text-gray-700 hover:text-orange-600 transition-all duration-200 shadow-sm hover:shadow-md group"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl group-hover:scale-125 transition-transform duration-200">{q.icon}</span>
                        <span className="text-sm font-medium">{q.text}</span>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 my-4">
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                  <span className="text-xs text-gray-400 font-semibold">OR</span>
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                </div>
              </div>
            )}

            {/* Enhanced Question Input */}
            <div className="mb-3">
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyPress={handleKeyPress}
                onFocus={() => {
                  // Auto-clear textarea when user clicks to type new question after getting an answer
                  if (answer) {
                    setQuestion('');
                    setAnswer(null);
                    setError(null);
                  }
                }}
                placeholder="Type your question here..."
                className="w-full p-4 border-2 border-gray-200 rounded-xl resize-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 text-sm transition-all duration-200 shadow-sm focus:shadow-md"
                rows={3}
                disabled={loading}
              />
              {error && (
                <div className="mt-2 p-2 bg-orange-50 border-l-4 border-orange-500 rounded">
                  <p className="text-xs text-orange-700 flex items-center gap-2">
                    <span>⚠️</span>
                    {error}
                  </p>
                </div>
              )}
            </div>

            {/* Enhanced Ask Button */}
            <button
              onClick={handleAsk}
              disabled={loading || !question.trim()}
              className="w-full p-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-bold hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-xl hover:scale-[1.02] mb-4"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span className="animate-pulse">Finding answer...</span>
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <span>🔍</span>
                  Ask Question
                </span>
              )}
            </button>
          </div>

          {/* Enhanced Footer */}
          <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-b-xl border-t border-gray-200">
            <p className="text-xs text-gray-600 text-center">
              Still need help? <a href="/contact" className="text-orange-600 hover:text-orange-700 font-bold underline decoration-orange-300 hover:decoration-orange-500 transition-all">Contact Support →</a>
            </p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }

        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out;
        }
      `}</style>
    </>
  );
}
